// react
import React, { useState, useMemo } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Box, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableFooter, TablePagination, Switch, IconButton, Typography, Button, Tooltip } from "@mui/material";
import { Add as AddIcon, KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, Delete as DeleteIcon } from "@mui/icons-material";

// A ---------------------------------------------------------------------- M

const ContactsTableToolbar = ({ handleOpen }) => {
  const { t } = useTranslation();

  const buttonStyle = {
    py: 0.5,
    borderColor: "divider",
    color: "text.primary",
    textTransform: "none",
    boxShadow: "none",
    borderRadius: 10,
    "&:hover": {
      borderColor: "primary.main",
      backgroundColor: "action.hover",
    },
  };

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" fontWeight="bold">
          {t("recipients")}
        </Typography>
      </Grid>
      <Grid item container xs={12} sm={6} justifyContent="flex-end">
        <Grid item>
          <Button variant="outlined" startIcon={<AddIcon />} sx={buttonStyle} onClick={handleOpen}>
            {t("new_recipient")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ContactsTableHead = () => {
  const { t } = useTranslation();

  const headCells = useMemo(
    () => [
      { id: "first_name", label: t("first_name") },
      { id: "last_name", label: t("last_name") },
      { id: "phone", label: t("phone") },
      { id: "enabled", label: t("status") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((cell) => (
          <TableCell key={cell.id} align={cell.id === "actions" ? "right" : "left"}>
            <Typography variant="body2" fontWeight="bold">
              {cell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const handleBack = (e) => onPageChange(e, page - 1);
  const handleNext = (e) => onPageChange(e, page + 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBack} disabled={page === 0}>
        <KeyboardArrowLeftOutlinedIcon />
      </IconButton>
      <IconButton onClick={handleNext} disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
        <KeyboardArrowRightOutlinedIcon />
      </IconButton>
    </Box>
  );
};

export const ContactsTable = ({ contacts, onToggleEnabled, onDeleteContact, handleOpen }) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - contacts.length) : 0;

  return (
    <Box>
      <ContactsTableToolbar handleOpen={handleOpen} />
      <TableContainer>
        <Table>
          <ContactsTableHead />
          <TableBody>
            {(rowsPerPage > 0 ? contacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : contacts).map((contact) => (
              <TableRow key={contact.id} hover>
                <TableCell>{contact.first_name || "-"}</TableCell>
                <TableCell>{contact.last_name || "-"}</TableCell>
                <TableCell>{contact.phone || "-"}</TableCell>
                <TableCell>
                  <Switch checked={!!contact.enabled} onChange={() => onToggleEnabled(contact.id, !!contact.enabled)} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t("remove")} placement="top">
                    <IconButton color="error" onClick={() => onDeleteContact(contact.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>

          <TableFooter sx={{ backgroundColor: "grey.100" }}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                colSpan={5}
                count={contacts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t("rows_per_page")}
                labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};
